import {
  Building2,
  Check,
  ChevronDown,
  LogOut,
  MessageSquare,
  Receipt,
  Settings,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger, cn } from '@command-center/ui-system';
import type { ClientOrganizationDetailResponse } from '@oneup/shared';
import { fetchClientDetail } from '../../api/client-data';
import { useOrganization } from '../../organization/OrganizationProvider';
import { useMessages } from '../../messages/MessagesProvider';
import { hasClientPortalCapability } from '@oneup/shared';

export function ClientWorkspaceMenu({
  compact,
  userName,
  userEmail,
  onSignOut,
}: {
  compact: boolean;
  userName?: string;
  userEmail?: string;
  onSignOut: () => void;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const organization = useOrganization();
  const messages = useMessages();
  const selected = organization.organizations.find(
    (item) => item.clientId === organization.selectedClientId,
  );
  const selectedDetail = useQuery({
    queryKey: ['client-organization-detail', organization.selectedClientId],
    queryFn: () => fetchClientDetail<ClientOrganizationDetailResponse>(
      '/api/client/organization',
      organization.selectedClientId!,
    ),
    enabled: Boolean(organization.selectedClientId),
    staleTime: 5 * 60_000,
    retry: 2,
    refetchOnWindowFocus: false,
  });
  const label = selected?.displayName ?? 'Client workspace';
  const selectedLogoUrl = selectedDetail.data?.organization.logoUrl ?? selected?.logoUrl;
  const accountLabel = userName || userEmail || 'Client Portal user';
  const messagesIncluded = hasClientPortalCapability(selected?.capabilities, 'messages');
  // Billing recovery remains available when paid service capabilities expire.
  const billingIncluded = Boolean(selected);

  const selectOrganization = (clientId: string) => {
    if (clientId === organization.selectedClientId) {
      setOpen(false);
      return;
    }
    setOpen(false);
    organization.selectClient(clientId);
    // Record routes are organization-specific. Returning to the Organizations
    // workspace prevents a detail URL from the prior organization producing a
    // confusing 404 after switching.
    navigate('/organizations/overview');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex min-h-12 w-full items-center gap-2.5 rounded-lg border border-transparent px-2 py-2 text-left transition-colors hover:border-border hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            compact && 'justify-center px-0',
          )}
          aria-label="Open organization switcher and account menu"
        >
          <WorkspaceMark label={label} logoUrl={selectedLogoUrl} />
          {!compact && (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{label}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {selected?.role ? formatRole(selected.role) : 'Your workspace'}
                </span>
              </span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" align="start" sideOffset={8} className="w-72 p-0">
        <div className="p-3">
          <p className="px-1 pb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            Organization
          </p>
          <div className="space-y-1">
            {organization.organizations.map((item) => {
              const active = item.clientId === organization.selectedClientId;
              return (
                <button
                  key={item.clientId}
                  type="button"
                  onClick={() => selectOrganization(item.clientId)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    active && 'bg-selection',
                  )}
                  aria-current={active ? 'true' : undefined}
                >
                  <WorkspaceMark label={item.displayName} logoUrl={item.logoUrl} small />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{item.displayName}</span>
                    <span className="block text-xs text-muted-foreground">
                      {formatRole(item.role)}
                    </span>
                  </span>
                  {active && (
                    <Check className="h-4 w-4 shrink-0 text-primary" aria-label="Selected" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-0.5 border-t p-2">
          {messagesIncluded && messages.settings?.messagesEnabled && (
            <MenuButton
              icon={<MessageSquare className="h-4 w-4" />}
              onClick={() => {
                setOpen(false);
                messages.openMessages();
              }}
            >
              Messages
            </MenuButton>
          )}
          {billingIncluded && (
            <MenuLink
              to="/billing"
              active={['/billing','/invoices','/subscriptions','/domains','/hosting'].some(path => pathname.startsWith(path))}
              icon={<Receipt className="h-4 w-4" />}
              onNavigate={() => setOpen(false)}
            >
              Billing
            </MenuLink>
          )}
          <MenuLink
            to="/settings"
            active={pathname.startsWith('/settings')}
            icon={<Settings className="h-4 w-4" />}
            onNavigate={() => setOpen(false)}
          >
            Settings
          </MenuLink>
        </div>

        <div className="border-t p-3">
          <div className="mb-2 flex items-center gap-2.5 px-2">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
              {initials(accountLabel)}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{accountLabel}</p>
              {userEmail && userEmail !== accountLabel && (
                <p className="truncate text-xs text-muted-foreground">{userEmail}</p>
              )}
            </div>
          </div>
          <div className="space-y-0.5">
            <button
              type="button"
              className="flex min-h-9 w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-destructive transition-colors hover:bg-destructive/10"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
            >
              <LogOut className="h-4 w-4" aria-hidden />
              Sign Out
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MenuLink({
  to,
  active,
  icon,
  children,
  onNavigate,
}: {
  to: string;
  active: boolean;
  icon: ReactNode;
  children: ReactNode;
  onNavigate: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className={cn(
        'flex min-h-9 items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted',
        active && 'bg-emphasis font-medium text-emphasis-foreground hover:bg-emphasis hover:text-emphasis-foreground focus-visible:bg-emphasis',
      )}
    >
      {icon}
      {children}
    </Link>
  );
}

function MenuButton({
  icon,
  children,
  onClick,
}: {
  icon: ReactNode;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-9 w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-muted"
    >
      {icon}
      {children}
    </button>
  );
}

function WorkspaceMark({
  label,
  logoUrl,
  small = false,
}: {
  label: string;
  logoUrl?: string;
  small?: boolean;
}) {
  const initials = label
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const sizeClass = small ? 'h-8 w-8' : 'h-9 w-9';
  if (logoUrl) {
    return (
      <span
        className={cn(
          'flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-card p-1',
          sizeClass,
        )}
      >
        <img
          src={logoUrl}
          alt={`${label} logo`}
          className="h-full w-full object-contain"
          loading="lazy"
        />
      </span>
    );
  }
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-lg bg-emphasis text-xs font-semibold text-emphasis-foreground',
        sizeClass,
      )}
      aria-hidden
    >
      {initials || <Building2 className="h-4 w-4" />}
    </span>
  );
}

function formatRole(role: string): string {
  return role.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

function initials(value: string): string {
  return value
    .split(/\s|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'CU';
}
